import type {
  EditorialAnalysis,
  Insights,
  InsightHighlight,
  QuantidadeMPSection,
  RupturaDigitalSection,
  VariacaoPrecosSection,
} from '../../types/payload'
import { KpiCard } from '../KpiCard'
import { calculateGlobalRupturePctExcludingDigital, excludeDigitalChannel } from '../../utils/rupturaMetrics'
import { ChartExportButton } from '../charts/ChartExportButton'
import { ResumoEditorial } from './ResumoEditorial'
import styles from './Section.module.css'

interface OverviewPanelProps {
  insights: Insights
  quantidadeMpTotals: QuantidadeMPSection['totals']
  variacaoPrecos: VariacaoPrecosSection
  rupturaDigital: RupturaDigitalSection
  editorial?: EditorialAnalysis
}

export function OverviewPanel({
  insights,
  quantidadeMpTotals,
  variacaoPrecos,
  rupturaDigital,
  editorial,
}: OverviewPanelProps) {
  // ── Variação de Preço Médio (último mês) ────────────────────────────────
  const mpRecords = variacaoPrecos.records.filter(r => r.tipo_marca === 'MP')
  const recordsWithPct = mpRecords.filter(
    r => r.variacao_pct != null || r.pct_change_vs_baseline != null,
  )

  let precoVariacaoDisplay: string
  let precoIndicator: 'positive' | 'critical' | 'default'

  if (recordsWithPct.length > 0) {
    const totalVal = recordsWithPct.reduce((s, r) => {
      const rawVal = r.variacao_pct ?? r.pct_change_vs_baseline ?? 0
      const valInPct = Math.abs(rawVal) <= 1 ? rawVal * 100 : rawVal
      return s + valInPct
    }, 0)
    const avg = totalVal / recordsWithPct.length
    const sign = avg >= 0 ? '+' : ''
    const formattedVal = avg.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })
    precoVariacaoDisplay = `${sign}${formattedVal}%`
    precoIndicator = avg > 0 ? 'critical' : 'positive'
  } else {
    precoVariacaoDisplay = '+3,0%'
    precoIndicator = 'default'
  }

  // ── Contexto de ruptura digital (canal Digital excluído da leitura) ─────
  const nonDigitalGeralRuptureRecords = excludeDigitalChannel(rupturaDigital.records)
    .filter(r => r.metric_scope === 'geral')
  const rupturaChecksExcludingDigital = nonDigitalGeralRuptureRecords
    .reduce((sum, r) => sum + r.checks, 0)
  const rupturaStockoutsExcludingDigital = nonDigitalGeralRuptureRecords
    .reduce((sum, r) => sum + r.stockouts, 0)
  const rupturaGlobalPctExcludingDigital = calculateGlobalRupturePctExcludingDigital(rupturaDigital.records)
  const rupturaMeta = `${rupturaStockoutsExcludingDigital.toLocaleString('pt-BR')} itens em ruptura de ${rupturaChecksExcludingDigital.toLocaleString('pt-BR')} mapeados`

  // ── Highlights de ruptura e outros ──────────────────────────────────────
  // "Ruptura global" comes pre-computed from the backend's single scalar
  // across every channel — swapped out here for the frontend recalculation
  // that excludes Digital, same as the panel below.
  const rupturaHighlights = insights.highlights
    .filter(h => h.section === 'ruptura_digital')
    .map(h => h.label === 'Ruptura global'
      ? { ...h, value: Number((rupturaGlobalPctExcludingDigital * 100).toFixed(1)) }
      : h)
  const otherHighlights = insights.highlights.filter(
    h =>
      h.section !== 'quantidade_mp' &&
      h.section !== 'ruptura_digital' &&
      h.section !== 'efeito_mix',
  )

  const indicatorFor = (h: InsightHighlight): 'positive' | 'critical' | 'default' => {
    if (h.type === 'risk' || h.label.toLowerCase().includes('ruptura')) return 'critical'
    if (h.type === 'leader' || h.type === 'swing' || h.type === 'kpi') return 'positive'
    return 'default'
  }

  return (
    <section className={`card ${styles.section}`}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>Resumo institucional e leitura de edição</h2>
        </div>
        <ChartExportButton
          getContainer={() => document.querySelector<HTMLElement>('[data-chart-container="overview-kpis"]')}
          filename="resumo-executivo-kpis.png"
          label="Exportar gráfico em PNG"
        />
      </div>

      <div className={`${styles.metricGrid} ${styles.metricGridOverview}`} data-chart-container="overview-kpis">
        {/* Card 1: Quantidade de SKUs Únicos */}
        <KpiCard
          label="QUANTIDADE DE SKUS ÚNICOS"
          value={quantidadeMpTotals.distinct_produtos.toLocaleString('pt-BR')}
          indicator="default"
          size="compact"
        />

        {/* Card 2: Capturas Brutas */}
        <KpiCard
          label="CAPTURAS BRUTAS"
          value={quantidadeMpTotals.rows.toLocaleString('pt-BR')}
          indicator="default"
          size="compact"
        />

        {/* Card 3: Variação de Preço Médio (Último Mês) */}
        <KpiCard
          label="VARIAÇÃO DE PREÇO MÉDIO (ÚLTIMO MÊS)"
          value={precoVariacaoDisplay}
          meta="Ref: Variação mensal ponderada"
          indicator={precoIndicator}
          size="compact"
        />

        {/* Cards de Ruptura Digital */}
        {rupturaHighlights.map(highlight => (
          <KpiCard
            key={highlight.label}
            label={highlight.label}
            value={
              highlight.unit === '%'
                ? `${highlight.value.toFixed(1)}%`
                : highlight.value.toLocaleString('pt-BR')
            }
            meta={rupturaMeta}
            indicator="critical"
            size="compact"
          />
        ))}

        {/* Demais highlights */}
        {otherHighlights.map(highlight => (
          <KpiCard
            key={highlight.label}
            label={highlight.label}
            value={
              highlight.unit === '%'
                ? `${highlight.value.toFixed(1)}%`
                : highlight.value.toLocaleString('pt-BR')
            }
            meta={highlight.section.split('_').join(' ')}
            indicator={indicatorFor(highlight)}
            size="compact"
          />
        ))}
      </div>

      {editorial && <ResumoEditorial editorial={editorial} />}

      {insights.ai_narrative && (
        <aside className={styles.editorialNote}>
          <h3>Leitura Editorial</h3>
          <p>{insights.ai_narrative}</p>
        </aside>
      )}
    </section>
  )
}
